import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

interface StoryblokStoreState {
  // Storyblok Assets
  storyblokAssets: any[];
  setStoryblokAssets: (storyblokAssets: any[]) => void;

  // Landing Page
  featuredPartners: any[];
  setFeaturedPartners: (featuredPartners: any[]) => void;
  workshopPartners: any[];
  setWorkshopPartners: (workshopPartners: any[]) => void;

  // Topic Page - Filter Options
  industryFilterOptions: any[];
  setIndustryFilterOptions: (industryFilterOptions: any[]) => void;
  assetTypeFilterOptions: any[];
  setAssetTypeFilterOptions: (assetTypeFilterOptions: any[]) => void;

  // Topic Page - Selected Filters
  selectedTheme: string;
  setSelectedTheme: (selectedTheme: string) => void;
  selectedIndustries: any[];
  setSelectedIndustries: (selectedIndustries: any[]) => void;
  selectedAssetTypes: any[];
  setSelectedAssetTypes: (selectedAssetTypes: any[]) => void;

  // Topic Page - Filtered Assets
  filteredAssets: any[];
  setFilteredAssets: (filteredAssets: any[]) => void;

  // Landing Page, Topic Page - Viewed Partners / Assets
  viewedAssets: any[];
  setViewedAssets: (viewedAssets: any[]) => void;
  viewedPartners: any[];
  setViewedPartners: (viewedPartners: any[]) => void;

  // Guide Page - Featured Assets
  guideFeaturedAssets: any[];
  setGuideFeaturedAssets: (guideFeaturedAssets: any[]) => void;

  // Guide Page - Featured Assets For Current Page
  guideFeaturedAssetsForCurrentPage: any[];
  setGuideFeaturedAssetsForCurrentPage: (
    guideFeaturedAssetsForCurrentPage: any[]
  ) => void;

  // Guide Page - Featured Assets Filter Options
  guideFeaturedAssetsIndustryFilterOptions: any[];
  setGuideFeaturedAssetsIndustryFilterOptions: (
    guideFeaturedAssetsIndustryFilterOptions: any[]
  ) => void;
  guideFeaturedAssetsAssetTypeFilterOptions: any[];
  setGuideFeaturedAssetsAssetTypeFilterOptions: (
    guideFeaturedAssetsAssetTypeFilterOptions: any[]
  ) => void;
  guideFeaturedAssetsCategoryFilterOptions: any[];
  setGuideFeaturedAssetsCategoryFilterOptions: (
    guideFeaturedAssetsCategoryFilterOptions: any[]
  ) => void;
  guideFeaturedAssetsTagFilterOptions: any[];
  setGuideFeaturedAssetsTagFilterOptions: (
    guideFeaturedAssetsCategoryFilterOptions: any[]
  ) => void;

  // Guide Page - Selected Featured Assets
  selectedGuideFeaturedAssetsIndustry: any[];
  setSelectedGuideFeaturedAssetsIndustry: (
    selectedGuideFeaturedAssetsIndustry: any[]
  ) => void;
  selectedGuideFeaturedAssetsAssetTypes: any[];
  setSelectedGuideFeaturedAssetsAssetTypes: (
    selectedGuideFeaturedAssetsAssetTypes: any[]
  ) => void;
  selectedGuideFeaturedAssetsCategories: any[];
  setSelectedGuideFeaturedAssetsCategories: (
    selectedGuideFeaturedAssetsCategories: any[]
  ) => void;
  selectedGuideFeaturedAssetsTags: any[];
  setSelectedGuideFeaturedAssetsTags: (
    selectedGuideFeaturedAssetsTags: any[]
  ) => void;
  guidePageFilterSelected: boolean;

  // Guide Page - Filtered Featured Assets
  filteredGuideFeaturedAssets: any[];
  setFilteredGuideFeaturedAssets: (filteredGuideFeaturedAssets: any[]) => void;

  // Theme Buttons
  themesWithoutFilters: any[];
  setThemesWithoutFilters: (themesWithoutFilters: any[]) => void;
  themesWithAdditionalComponents: any[];
  setThemesWithAdditionalComponents: (
    themesWithAdditionalComponents: any[]
  ) => void;

  // Collection
  collection: any[];
  setCollection: (collection: any[]) => void;
}

export const useStoryblokStore = create<StoryblokStoreState>()(set => ({
  // Storyblok Assets
  storyblokAssets: [],
  setStoryblokAssets: (storyblokAssets: any[]) => set({ storyblokAssets }),

  // Landing Page
  featuredPartners: [],
  setFeaturedPartners: (featuredPartners: any[]) => set({ featuredPartners }),
  workshopPartners: [],
  setWorkshopPartners: (workshopPartners: any[]) => set({ workshopPartners }),

  // Topic Page - Filter Options
  industryFilterOptions: [],
  setIndustryFilterOptions: (industryFilterOptions: any[]) =>
    set({ industryFilterOptions }),
  assetTypeFilterOptions: [],
  setAssetTypeFilterOptions: (assetTypeFilterOptions: any[]) =>
    set({ assetTypeFilterOptions }),

  // Topic Page - Selected Filters
  selectedTheme: '',
  setSelectedTheme: (selectedTheme: string) => set({ selectedTheme }),
  selectedIndustries: [],
  setSelectedIndustries: (selectedIndustries: any[]) =>
    set({
      selectedIndustries,
    }),
  selectedAssetTypes: [],
  setSelectedAssetTypes: (selectedAssetTypes: any[]) =>
    set({
      selectedAssetTypes,
    }),

  // Topic Page - Filtered Assets
  filteredAssets: [],
  setFilteredAssets: (filteredAssets: any[]) => set({ filteredAssets }),

  // Landing Page, Topic Page - Viewed Partners / Assets
  viewedAssets: [],
  setViewedAssets: (viewedAssets: any[]) => set({ viewedAssets }),
  viewedPartners: [],
  setViewedPartners: (viewedPartners: any[]) => set({ viewedPartners }),

  // Guide Page - Featured Assets
  guideFeaturedAssets: [],
  setGuideFeaturedAssets: (guideFeaturedAssets: any[]) =>
    set({ guideFeaturedAssets }),

  // Guide Page - Featured Assets for current page
  guideFeaturedAssetsForCurrentPage: [],
  setGuideFeaturedAssetsForCurrentPage: (
    guideFeaturedAssetsForCurrentPage: any[]
  ) => set({ guideFeaturedAssetsForCurrentPage }),

  // Guide Page - Featured Assets Filter Options
  guideFeaturedAssetsIndustryFilterOptions: [],
  setGuideFeaturedAssetsIndustryFilterOptions: (
    guideFeaturedAssetsIndustryFilterOptions: any[]
  ) => set({ guideFeaturedAssetsIndustryFilterOptions }),
  guideFeaturedAssetsAssetTypeFilterOptions: [],
  setGuideFeaturedAssetsAssetTypeFilterOptions: (
    guideFeaturedAssetsAssetTypeFilterOptions: any[]
  ) => set({ guideFeaturedAssetsAssetTypeFilterOptions }),
  guideFeaturedAssetsCategoryFilterOptions: [],
  setGuideFeaturedAssetsCategoryFilterOptions: (
    guideFeaturedAssetsCategoryFilterOptions: any[]
  ) => set({ guideFeaturedAssetsCategoryFilterOptions }),
  guideFeaturedAssetsTagFilterOptions: [],
  setGuideFeaturedAssetsTagFilterOptions: (
    guideFeaturedAssetsTagFilterOptions: any[]
  ) => set({ guideFeaturedAssetsTagFilterOptions }),

  // Guide Page - Selected Featured Assets Asset Type and Categories
  selectedGuideFeaturedAssetsIndustry: [],
  setSelectedGuideFeaturedAssetsIndustry: (
    selectedGuideFeaturedAssetsIndustry: any[]
  ) =>
    set({
      selectedGuideFeaturedAssetsIndustry,
      guidePageFilterSelected: selectedGuideFeaturedAssetsIndustry.length > 0,
    }),
  selectedGuideFeaturedAssetsAssetTypes: [],
  setSelectedGuideFeaturedAssetsAssetTypes: (
    selectedGuideFeaturedAssetsAssetTypes: any[]
  ) =>
    set({
      selectedGuideFeaturedAssetsAssetTypes,
      guidePageFilterSelected: selectedGuideFeaturedAssetsAssetTypes.length > 0,
    }),
  selectedGuideFeaturedAssetsCategories: [],
  setSelectedGuideFeaturedAssetsCategories: (
    selectedGuideFeaturedAssetsCategories: any[]
  ) =>
    set({
      selectedGuideFeaturedAssetsCategories,
      guidePageFilterSelected: selectedGuideFeaturedAssetsCategories.length > 0,
    }),
  selectedGuideFeaturedAssetsTags: [],
  setSelectedGuideFeaturedAssetsTags: (
    selectedGuideFeaturedAssetsTags: any[]
  ) =>
    set({
      selectedGuideFeaturedAssetsTags,
      guidePageFilterSelected: selectedGuideFeaturedAssetsTags.length > 0,
    }),
  guidePageFilterSelected: false,

  // Guide Page - Filtered Featured Assets
  filteredGuideFeaturedAssets: [],
  setFilteredGuideFeaturedAssets: (filteredGuideFeaturedAssets: any[]) =>
    set({ filteredGuideFeaturedAssets }),

  // Theme Buttons
  themesWithoutFilters: [],
  setThemesWithoutFilters: (themesWithoutFilters: any[]) =>
    set({ themesWithoutFilters }),
  themesWithAdditionalComponents: [],
  setThemesWithAdditionalComponents: (themesWithAdditionalComponents: any[]) =>
    set({ themesWithAdditionalComponents }),

  // Collection
  collection: [],
  setCollection: collection => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('collection', JSON.stringify(collection));
    }
    set({ collection });
  },
}));

export const useTopicPageFilterSelected = () => {
  return useStoryblokStore(
    useShallow(
      state =>
        !!state.selectedTheme ||
        state.selectedIndustries.length > 0 ||
        state.selectedAssetTypes.length > 0
    )
  );
};
